import { useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { RefreshCw } from "lucide-react";
import { RootState } from "../../../store";
import { getListMediaLogs } from "@/store/constants/constants.action";
import { getUpdateMedia } from "@/store/media/media.action";
import Pagination from "@/components/Table/Pagination";
import SectionHeader from "@/components/Layout/SectionHeader";
import { useMediaLogs } from "./useMediaLogs";
import LogFilters from "./LogFilters";
import LogEntry from "./LogEntry";

const MediaLogs = ({
  mediaLogsList,
  getListMediaLogs,
  getUpdateMedia,
  currentMediaDetailsForEdit,
  userList,
}: Props) => {
  const {
    mediaId,
    searchQuery,
    setSearchQuery,
    dateFilter,
    systemLogs,
    filters,
    revertingKey,
    isLoading,
    totalCount,
    filteredLogs,
    fetchLogs,
    changeDateFilter,
    toggleSystemLogs,
    handleSetFilters,
    revertField,
    revertAll,
    resolveUserName,
  } = useMediaLogs({
    mediaLogsList,
    currentMedia: currentMediaDetailsForEdit,
    userList,
    getListMediaLogs,
    getUpdateMedia,
  });

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div
      className="w-100 p-3"
      style={{ maxWidth: "1100px", paddingBottom: "90px" }}
    >
      <SectionHeader
        title="Activity Logs"
        subtitle="Complete lifecycle of this video · Newest first"
        buttonText="Refresh"
        buttonIcon={<RefreshCw size={14} />}
        onButtonClick={() => fetchLogs()}
      />

      <LogFilters
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        systemLogs={systemLogs}
        onToggleSystemLogs={toggleSystemLogs}
        dateFilter={dateFilter}
        onDateFilterChange={changeDateFilter}
      />

      {/* Timeline */}
      {isLoading ? (
        <div className="text-center py-5 text-secondary">
          <div
            className="spinner-border spinner-border-sm me-2"
            role="status"
          />
          Loading logs…
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="text-center py-5 text-secondary">No logs found.</div>
      ) : (
        <div className="position-relative">
          {/* Vertical line through the dot centers (16px) */}
          <div
            className="position-absolute bg-border"
            style={{
              left: 16,
              top: 16,
              bottom: 16,
              width: 1,
              backgroundColor: "#dee2e6",
            }}
          />
          <div className="d-flex flex-column gap-3">
            {filteredLogs.map((log) => (
              <LogEntry
                key={log.id}
                log={log}
                userName={resolveUserName(log.userId)}
                isExpanded={expanded.has(log.id)}
                onToggle={toggleExpand}
                revertingKey={revertingKey}
                mediaId={mediaId}
                onRevert={revertField}
                onRevertAll={revertAll}
              />
            ))}
          </div>

          {/* Timeline end */}
          <div className="d-flex gap-3 mt-3 align-items-center">
            <div
              className="flex-shrink-0 rounded-circle bg-light border d-flex align-items-center justify-content-center"
              style={{ width: 32, height: 32, zIndex: 1 }}
            >
              <div
                className="rounded-circle bg-secondary"
                style={{ width: 8, height: 8, opacity: 0.4 }}
              />
            </div>
            <span className="text-secondary small fst-italic">
              Video lifecycle started here
            </span>
          </div>
          <Pagination
            total={totalCount || 0}
            filters={filters}
            initialPage={filters.page}
            handleSetFilters={handleSetFilters}
            onChange={() => {}}
            showRowsPerPage={true}
          />
        </div>
      )}
    </div>
  );
};

const mapState = (state: RootState) => ({
  mediaLogsList: state.constants.mediaLogsList,
  currentMediaDetailsForEdit: state.media.mediaDetails.media,
  userList: state.users.userList,
});

const mapDispatch = {
  getListMediaLogs,
  getUpdateMedia,
};

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector>;
export default connector(MediaLogs);
